import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import * as DataList from 'components/DataList';
import { observer, inject } from 'mobx-react';
import Page from 'components/Page';
import UserProfileCard, { UserPageSkeleton } from 'components/User/ProfileCard';
import AccountSidebar from 'components/AccountSidebar';
import styled from '@emotion/styled';

const PageWrapGray = styled('div')`
  padding: 5px;
  background-color: #F8FAFB;
  min-height: calc(100vh - 75px);
`

// A team member's page (/dashboard/users/:id/show).
@inject('store', 'viewStore', 'userStore') @observer
class UsersShowPage extends Component {

  componentWillUnmount() {
    this.props.userStore.clearSelectedUser();
  }

  // Your own profile (the account menu's Profile) gets the account menu beside
  // it; another team member's page doesn't. Read from the address, so it's
  // known before the user has loaded.
  get isOwnProfile() {
    const me = this.props.viewStore.currentUser;
    const id = window.location.pathname.split('/').filter(Boolean).slice(-2)[0];
    return Boolean(me && me.id && id === String(me.id));
  }

  retry = () => {
    const uuid = window.location.pathname.split('/').filter(Boolean).slice(-2)[0];
    this.props.userStore.loadUser({ uuid });
  };

  // A user that doesn't exist (removed, or a wrong link) gets a way back;
  // anything else (offline, server error) gets Try again.
  renderError() {
    const { userStore: { user }, viewStore } = this.props;
    const missing = user.errorStatus === 404 || user.errorStatus === 403;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const back = { label: 'Back to the team', icon: 'arrow-left', onClick: () => viewStore.users() };
    return (
      <div className="cp-error">
        {missing ? (
          <DataList.Notice
            icon="person"
            title="This person isn't on your team"
            message="They may have been removed, or the link may be wrong. Find them in your team list instead."
            actions={[{ ...back, primary: true }]}
          />
        ) : (
          <DataList.Notice
            tone="error"
            icon={offline ? 'offline' : 'error'}
            role="alert"
            title="This team member couldn't be loaded"
            message={offline
              ? "You're offline. Reconnect, then try again."
              : 'Something went wrong on our side. Try again in a moment.'}
            actions={[{ label: 'Try again', icon: 'refresh', onClick: this.retry, primary: true }, back]}
          />
        )}
      </div>
    );
  }

  render() {
    const { userStore: { user: { isLoading, isError, object } } } = this.props;
    let content = null;
    let title;
    // Once a user is shown it stays on screen while it reloads (e.g. after a
    // vacation is deleted); the skeleton is only for the first load.
    if (object && object.attributes) {
      title = `${object.attributes.full_name} | Field Nexus`;
      content = <UserProfileCard key={object.id} user={object} />;
    } else if (isError && !isLoading) {
      title = `${this.props.userStore.user.errorStatus === 404 ? 'Team member not found' : "Couldn't load team member"} | Field Nexus`;
      content = this.renderError();
    } else if (isLoading) {
      title = 'Loading..';
      content = <UserPageSkeleton />;
    }
    const page = (
      <PageWrapGray>
        <Page>
          {content}
        </Page>
      </PageWrapGray>
    );
    return (
      <div>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {this.isOwnProfile ? (
          <div className="row no-gutters">
            <div className="col-lg-2 sidebar-responsive">
              <AccountSidebar />
            </div>
            <div className="col-lg-10">{page}</div>
          </div>
        ) : page}
      </div>
    );
  }
}

export default UsersShowPage;
